import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { MemberView, MessagePart } from "@/interfaces/Chat";
import {
  compactParts,
  editParts,
  plainText,
  TextEditRange,
} from "@/helpers/chatText";
type MentionMember = MemberView & { email?: string };
export default function MentionInput({
  parts,
  onChange,
  members,
  disabled = false,
  onSend,
  onSearch,
  onMore,
  hasMore = false,
}: {
  parts: MessagePart[];
  onChange: (parts: MessagePart[]) => void;
  members: MentionMember[];
  disabled?: boolean;
  onSend?: () => void;
  onSearch?: (query: string | null) => void;
  onMore?: () => void;
  hasMore?: boolean;
}) {
  const editor = useRef<HTMLTextAreaElement>(null),
    composing = useRef(false),
    listId = useId();
  const selection = useRef<{
    value: string;
    range: TextEditRange;
    inputType: string;
  } | null>(null);
  const compositionRange = useRef<TextEditRange | null>(null);
  const latest = useRef({ parts, onChange });
  latest.current = { parts, onChange };
  const capture = useCallback(
    (element: HTMLTextAreaElement, inputType = "") => {
      selection.current = {
        value: element.value,
        range: { start: element.selectionStart, end: element.selectionEnd },
        inputType,
      };
    },
    []
  );
  useEffect(() => {
    const element = editor.current;
    if (!element) return;
    const beforeInput = (event: Event) => {
      const inputType = (event as InputEvent).inputType || "";
      capture(element, inputType);
      // An identical-text paste/replacement may not fire React onChange. Strip
      // IDs in the selected replacement now, while its native range is known.
      const range = selection.current!.range;
      if (
        inputType.startsWith("insert") &&
        range.start !== range.end &&
        !composing.current
      ) {
        const current = latest.current;
        const next = editParts(current.parts, element.value, range);
        if (JSON.stringify(next) !== JSON.stringify(current.parts))
          current.onChange(next);
      }
    };
    element.addEventListener("beforeinput", beforeInput);
    return () => element.removeEventListener("beforeinput", beforeInput);
  }, [capture]);
  const [query, setQuery] = useState<{
      start: number;
      end: number;
      text: string;
    } | null>(null),
    [selected, setSelected] = useState(0);
  const text = plainText(parts);
  const suggestions = query
    ? members.filter(
        (m) =>
          m.active &&
          m.id &&
          m.name.toLocaleLowerCase().includes(query.text.toLocaleLowerCase())
      )
    : [];
  const close = () => {
    setQuery(null);
    onSearch?.(null);
  };
  const search = (value: string, caret: number) => {
    const match = /(?:^|\s)@([^@\n]*)$/.exec(value.slice(0, caret));
    if (!match) {
      close();
      return;
    }
    const next = {
      start: caret - match[1].length - 1,
      end: caret,
      text: match[1],
    };
    setQuery(next);
    setSelected(0);
    onSearch?.(next.text);
  };
  const choose = (member: MentionMember) => {
    if (!query || !member.id) return;
    const before = editParts(parts, text.slice(0, query.start), {
      start: query.start,
      end: text.length,
    });
    // Split at the caret through the same edit reconciliation, keeping IDs in
    // untouched prefix/suffix tokens rather than flattening the whole draft.
    const after = editParts(parts, text.slice(query.end), {
      start: 0,
      end: query.end,
    });
    const next = compactParts([
      ...before,
      { type: "mention", userId: member.id, label: member.name },
      { type: "text", text: " " },
      ...after,
    ]);
    onChange(next);
    close();
    const caret = query.start + member.name.length + 2;
    requestAnimationFrame(() => {
      editor.current?.focus();
      editor.current?.setSelectionRange(caret, caret);
    });
  };
  const keyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!composing.current && !e.nativeEvent.isComposing && e.keyCode !== 229) {
      compositionRange.current = null;
      capture(
        e.currentTarget,
        e.key === "Backspace"
          ? "deleteContentBackward"
          : e.key === "Delete"
          ? "deleteContentForward"
          : "insertText"
      );
    }
    if (composing.current || e.nativeEvent.isComposing || e.keyCode === 229)
      return;
    if (query && e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (
      query &&
      suggestions.length &&
      ["ArrowDown", "ArrowUp"].includes(e.key)
    ) {
      e.preventDefault();
      setSelected(
        (i) =>
          (i + (e.key === "ArrowDown" ? 1 : -1) + suggestions.length) %
          suggestions.length
      );
      return;
    }
    if (query && suggestions.length && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      choose(suggestions[selected % suggestions.length]);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend?.();
    }
  };
  return (
    <div className="chat-mention-input">
      <textarea
        ref={editor}
        className="form-control"
        rows={2}
        aria-label="Écrire un message"
        placeholder="Écrire un message…"
        value={text}
        disabled={disabled}
        aria-autocomplete="list"
        aria-controls={query ? listId : undefined}
        aria-expanded={!!query}
        aria-activedescendant={
          query && suggestions.length
            ? `${listId}-${selected % suggestions.length}`
            : undefined
        }
        onCompositionStart={(e) => {
          composing.current = true;
          compositionRange.current = {
            start: e.currentTarget.selectionStart,
            end: e.currentTarget.selectionEnd,
          };
          capture(e.currentTarget, "insertCompositionText");
        }}
        onCompositionEnd={() => {
          composing.current = false;
        }}
        onSelect={(e) => capture(e.currentTarget)}
        onPaste={(e) => {
          compositionRange.current = null;
          capture(e.currentTarget, "insertFromPaste");
        }}
        onCut={(e) => {
          compositionRange.current = null;
          capture(e.currentTarget, "deleteByCut");
        }}
        onChange={(e) => {
          const value = e.target.value;
          const previous = selection.current;
          const inputType =
            (e.nativeEvent as InputEvent).inputType ||
            previous?.inputType ||
            "";
          let range =
            previous?.value === text ? { ...previous.range } : undefined;
          if (
            compositionRange.current &&
            (composing.current || inputType.includes("Composition"))
          )
            range = { ...compositionRange.current };
          if (
            range &&
            range.start === range.end &&
            inputType.startsWith("delete")
          ) {
            const removed = text.length - value.length;
            if (inputType.endsWith("Backward")) range.start -= removed;
            else if (inputType.endsWith("Forward")) range.end += removed;
          }
          onChange(editParts(parts, value, range));
          if (range && composing.current)
            compositionRange.current = {
              start: range.start,
              end:
                range.start +
                value.length -
                (text.length - (range.end - range.start)),
            };
          selection.current = null;
          search(value, e.target.selectionStart);
        }}
        onClick={(e) => {
          if (!composing.current) compositionRange.current = null;
          capture(e.currentTarget);
          search(e.currentTarget.value, e.currentTarget.selectionStart);
        }}
        onKeyDown={keyDown}
      />
      {query && (
        <div className="chat-mention-suggestions">
          <ul id={listId} role="listbox" aria-label="Membres à mentionner">
            {suggestions.map((member, i) => (
              <li
                key={member.id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === selected}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(member)}
              >
                <button type="button" tabIndex={-1}>
                  <strong>{member.name}</strong>{" "}
                  <small>
                    {member.email ||
                      `${
                        member.role === "admin" ? "Administrateur" : "Membre"
                      } · ${member.id}`}
                  </small>
                </button>
              </li>
            ))}
            {!suggestions.length && <li>Aucun membre actif trouvé.</li>}
          </ul>
          {hasMore && (
            <button type="button" onClick={onMore}>
              Autres membres
            </button>
          )}
        </div>
      )}
      {parts.some((p) => p.type === "mention") && (
        <span className="visually-hidden">
          Les mentions sélectionnées suivent les noms affichés. Modifier un nom
          retire sa mention.
        </span>
      )}
    </div>
  );
}
